import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text } from "tamagui";
import {
  RiArrowLeftSLine,
  RiBankCardLine,
  RiSecurePaymentLine,
  // RiShieldCheckLine,（安全提示隱藏中）
  RiErrorWarningLine,
} from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { IdentityContext } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import appToast from "@components/Toast";
import {
  handleAPIListContextMethods,
  handleAPISetContextDefault,
  handleAPIListPaymentMethods,
  handleAPIGrantAuthorization,
  type ContextPaymentMethod,
  type OwnedPaymentMethod,
} from "./API/handlePaymentAPI";
import { CardBrand } from "@components/payment/PaymentOptionRow";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import InfoNote from "./components/ui/InfoNote";
import BottomSheet from "./components/ui/BottomSheet";
import { AccountMethod, typeStyleMap, EmptyState, AccountMethodRow, AddCardFooter } from "./components/accountPayments/methodRow";

/**
 * 付款方式頁。
 * 範圍：單一瓦斯行帳戶 — 管理此帳戶可用的支付工具與成員授權。
 * 資料來自 GET /customer/contexts/:id/payment-methods；綁卡兩路徑導去 TapPay 表單。
 */
export const AccountPayments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextId } = useParams();

  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const back = () => navigate(`/account/detail/${contextId}`, { state: { context: ctx } });

  const { cusJwtToken } = useCusAuth();


  // 帳戶可用付款方式（GET /customer/contexts/:id/payment-methods）
  const [methods, setMethods] = useState<ContextPaymentMethod[]>([]);
  const [defaultId, setDefaultId] = useState<string>("");

  const loadMethods = () => {
    if (!cusJwtToken || !contextId) return;
    handleAPIListContextMethods(contextId, cusJwtToken).then((r) => {
      if (r.state !== "success") return;
      setMethods(r.items);
      setDefaultId(r.defaultId ?? "");
    });
  };
  useEffect(loadMethods, [cusJwtToken, contextId]);

  const toAccountMethod = (m: ContextPaymentMethod): AccountMethod => ({
    id: m.payment_method_id,
    type: m.method_type === "line_pay" ? "line" : "card",
    name: m.nickname || (m.card?.lastfour ? `**** ${m.card.lastfour}` : "信用卡"),
    isDefault: m.is_default,
  });

  // 三層結構（2026-08-13 拍板）：
  //   帳戶共用     = 總表（帳戶專屬卡 + 成員加入的卡）
  //   帳戶專屬卡   = context 卡（屬於帳戶、所有成員可用、不隨成員去留；限負責人綁）
  //   成員加入的卡 = 個人卡加進帳戶（含負責人的個人卡；限加入者本人使用、退出時自動解除）
  const contextCards = methods.filter((m) => m.card_scope === "context");
  const memberCards = methods.filter((m) => m.card_scope !== "context");
  const mergedCards = [...contextCards, ...memberCards];
  const cardSub = (m: ContextPaymentMethod) =>
    m.card_scope === "context" ? `帳戶卡 · 由 ${m.added_by_name || "負責人"} 綁定` : `由 ${m.added_by_name || "成員"} 加入`;

  const allMethods: AccountMethod[] = mergedCards.map(toAccountMethod);
  const defaultMethod = allMethods.find((m) => m.id === defaultId) ?? null;

  // 設為預設（PATCH /customer/contexts/:id/payment-default）
  const setDefault = async (methodId: string) => {
    if (!cusJwtToken || !contextId || methodId === defaultId) return;
    const prev = defaultId;
    setDefaultId(methodId); // 樂觀更新
    const r = await handleAPISetContextDefault(contextId, methodId, cusJwtToken);
    if (r.state !== "success") {
      setDefaultId(prev);
      appToast.error(typeof r.error === "string" ? r.error : "設定預設失敗");
    }
  };


  // 綁新卡 BottomSheet（選綁卡路徑 → 選既有卡授權 / 導去 TapPay 表單）
  const [sheetOpen, setSheetOpen] = useState(false);
  // sheet 步驟：menu = 兩路徑選單；pick = 從個人錢包選既有卡
  const [sheetStep, setSheetStep] = useState<"menu" | "pick">("menu");
  const [ownedCards, setOwnedCards] = useState<OwnedPaymentMethod[]>([]);
  const [ownedLoading, setOwnedLoading] = useState(false);
  const [granting, setGranting] = useState<string | null>(null);
  const openSheet = () => {
    setSheetStep("menu");
    setSheetOpen(true);
  };
  const closeSheet = () => setSheetOpen(false);

  // 路徑 A：先列出我已綁的個人信用卡（選卡授權，免重綁）
  const openPickCard = () => {
    if (!cusJwtToken) return;
    setSheetStep("pick");
    setOwnedLoading(true);
    setSheetOpen(true);
    handleAPIListPaymentMethods(cusJwtToken)
      .then((r) => {
        setOwnedCards(r.state === "success" ? r.items.filter((c) => c.method_type === "credit_card" && c.status === "active") : []);
      })
      .finally(() => setOwnedLoading(false));
  };

  // 已在此帳戶可用清單中的卡（不可重複加入）
  const inContextIds = new Set(methods.map((m) => m.payment_method_id));

  // 選定既有卡 → self-authorize 到此帳戶
  const grantCard = async (card: OwnedPaymentMethod) => {
    if (!cusJwtToken || !contextId || granting) return;
    setGranting(card.payment_method_id);
    const r = await handleAPIGrantAuthorization(card.payment_method_id, contextId, cusJwtToken);
    setGranting(null);
    if (r.state === "success") {
      appToast.success("已將卡片加入此帳戶");
      closeSheet();
      loadMethods();
    } else {
      appToast.error(r.error || "加入失敗，請稍後再試");
    }
  };

  /**
   * 綁卡兩路徑（§七）：
   *   - personal：從個人錢包加入 → 綁個人卡並授權給此帳戶（share_with_context）。
   *   - context ：為此帳戶綁新卡 → owner 綁一張帳戶（公司）卡。
   * 共用既有 /account/paymentMethod 的 TapPay 表單，透過 state 帶 scope + context。
   */
  const goBind = (scope: "personal" | "context") => {
    setSheetOpen(false);
    navigate("/account/paymentMethod", {
      state: {
        context: ctx,
        bindScope: scope,
        shareWithContext: scope === "personal",
        returnTo: `/account/detail/${contextId}/payments`,
        returnContext: { type: "wallet" },
      },
    });
  };

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack ai="center" onPress={back} cursor="pointer" mb="$3">
          <View mr="$2">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <Text color={colors.light} fontWeight="bold" fontSize={16}>
            返回
          </Text>
        </XStack>
        <Text color={colors.light} fontSize={22} fontWeight="800" mb="$1.5">
          付款方式
        </Text>
        <Text color="rgba(255,255,255,0.85)" fontSize={12}>
          管理此帳戶可用的支付工具與成員授權
        </Text>
      </Header>

      <RadiusContentWrapper space="$4">
        {/* 1. 下單時預設使用 */}
        <YStack>
          <SectionLabel title="下單時預設使用" />
          {defaultMethod ? (
            <XStack className="primary-bg-gradient" ai="center" gap="$3" br={14} px="$3.5" py="$3.5">
              <View w={40} h={40} br={10} bg="rgba(255,255,255,0.2)" ai="center" jc="center" flexShrink={0}>
                <RiSecurePaymentLine size={20} color={colors.light} />
              </View>
              <YStack f={1} miw={0}>
                <Text fontSize={11} color="rgba(255,255,255,0.85)" mb="$1">
                  目前預設
                </Text>
                <Text fontSize={15} fontWeight="800" color={colors.light} numberOfLines={1}>
                  {defaultMethod.name}
                </Text>
              </YStack>
              <View br={10} px="$2.5" py="$1" bg="rgba(255,255,255,0.22)" flexShrink={0}>
                <Text fontSize={11} fontWeight="700" color={colors.light}>
                  {typeStyleMap[defaultMethod.type].label}
                </Text>
              </View>
            </XStack>
          ) : (
            <XStack bg={colors.bgAmber} br={14} px="$3.5" py="$3.5" gap="$3" ai="center">
              <View w={40} h={40} br={10} bg="rgba(245,158,11,0.18)" ai="center" jc="center" flexShrink={0}>
                <RiErrorWarningLine size={20} color={colors.amber} />
              </View>
              <YStack f={1} miw={0}>
                <Text fontSize={14} fontWeight="700" color={colors.amberDark}>
                  尚未設定預設付款
                </Text>
                <Text fontSize={11} color={colors.amberDark} mt="$1">
                  於下方任一付款工具點「設為預設」
                </Text>
              </YStack>
            </XStack>
          )}
        </YStack>

        {/* 2. 帳戶共用（總表 = 帳戶專屬卡 + 成員加入的卡；設為預設在這裡操作） */}
        <YStack>
          <SectionLabel
            title="帳戶共用"
            right={
              <View br={10} px="$2" py="$0.5" bg={colors.bgPurple}>
                <Text fontSize={10} fontWeight="700" color={colors.purpleDark}>
                  此帳戶可用的卡
                </Text>
              </View>
            }
          />
          <ListContainer>
            {mergedCards.length === 0 ? (
              <EmptyState text="尚未加入任何卡片" />
            ) : (
              mergedCards.map((m, i) => (
                <AccountMethodRow
                  key={m.payment_method_id}
                  method={toAccountMethod(m)}
                  sub={cardSub(m)}
                  isDefault={defaultId === m.payment_method_id}
                  onSetDefault={() => setDefault(m.payment_method_id)}
                  isLast={i === mergedCards.length - 1}
                />
              ))
            )}
            <AddCardFooter label="新增付款卡" onPress={openSheet} />
          </ListContainer>
        </YStack>

        {/* 3. 帳戶專屬卡（context 卡：屬於帳戶、所有成員可用、不隨成員去留） */}
        <YStack>
          <SectionLabel
            title="帳戶專屬卡"
            right={
              <View br={10} px="$2" py="$0.5" bg={colors.bgPurple}>
                <Text fontSize={10} fontWeight="700" color={colors.purpleDark}>
                  所有成員可用
                </Text>
              </View>
            }
          />
          <ListContainer>
            {contextCards.length === 0 ? (
              <EmptyState text="尚未為此帳戶綁定專屬卡" />
            ) : (
              contextCards.map((m, i) => (
                <AccountMethodRow
                  key={m.payment_method_id}
                  method={toAccountMethod(m)}
                  sub={`由 ${m.added_by_name || "負責人"} 綁定 · 不隨成員去留`}
                  isDefault={defaultId === m.payment_method_id}
                  onSetDefault={() => setDefault(m.payment_method_id)}
                  isLast={i === contextCards.length - 1}
                />
              ))
            )}
            <AddCardFooter label="為此帳戶綁新卡（限負責人）" onPress={() => goBind("context")} />
          </ListContainer>
        </YStack>

        {/* 4. 成員加入的卡（個人卡加進帳戶，含負責人的個人卡；限加入者本人使用、退出時自動解除） */}
        <YStack>
          <SectionLabel
            title="成員加入的卡"
            right={
              <View br={10} px="$2" py="$0.5" bg={colors.bgGreen}>
                <Text fontSize={10} fontWeight="700" color={colors.greenText}>
                  限本人使用
                </Text>
              </View>
            }
          />
          <ListContainer>
            {memberCards.length === 0 ? (
              <EmptyState text="尚無成員加入個人卡" />
            ) : (
              memberCards.map((m, i) => (
                <AccountMethodRow
                  key={m.payment_method_id}
                  method={toAccountMethod(m)}
                  sub={`由 ${m.added_by_name || "成員"} 加入 · 退出帳戶時自動解除`}
                  isDefault={defaultId === m.payment_method_id}
                  onSetDefault={() => setDefault(m.payment_method_id)}
                  isLast={i === memberCards.length - 1}
                />
              ))
            )}
            <AddCardFooter label="從個人錢包加入" onPress={openPickCard} />
          </ListContainer>
        </YStack>

        {/* TODO(account-hide): 安全提示（授權推播/成員權限撤回）——授權管理 UI 已移除、推播提醒未實作，暫時隱藏。詳見 docs/account-hidden-sections.md */}
        {/* <InfoNote tone="primary" icon={<RiShieldCheckLine size={15} color={colors.primaryText} />}>
          授權成員每次刷你的卡，你都會收到推播 · 可在「成員權限」隨時撤回授權。{" "}
          <Text
            fontWeight="700"
            color={colors.primary}
            cursor="pointer"
            onPress={() => navigate(`/account/detail/${contextId}/members`, { state: { context: ctx } })}
            style={{ textDecorationLine: "underline" }}
          >
            前往管理
          </Text>
        </InfoNote> */}
      </RadiusContentWrapper>

      {/* 7. 綁新卡 BottomSheet */}
      <BottomSheet open={sheetOpen} onClose={closeSheet}>
        {sheetStep === "pick" ? (
          <YStack px="$4" pb="$5" gap="$3.5">
            <XStack ai="center" gap="$2.5">
              <View w={36} h={36} br={9} bg={colors.primaryBg} ai="center" jc="center">
                <RiBankCardLine size={18} color={colors.primary} />
              </View>
              <Text fontSize={17} fontWeight="800" color={colors.text}>
                從個人錢包加入
              </Text>
            </XStack>
            <Text fontSize={12} color={colors.sub}>
              選一張你已綁定的信用卡加入此帳戶（免重新綁卡）。
            </Text>

            {ownedLoading ? (
              <Text fontSize={13} color={colors.hint} ta="center" py="$4">
                載入中…
              </Text>
            ) : ownedCards.length === 0 ? (
              <Text fontSize={13} color={colors.hint} ta="center" py="$4">
                你還沒有綁定任何信用卡
              </Text>
            ) : (
              <YStack gap="$2">
                {ownedCards.map((c) => {
                  const joined = inContextIds.has(c.payment_method_id);
                  const busy = granting === c.payment_method_id;
                  return (
                    <XStack
                      key={c.payment_method_id}
                      ai="center"
                      gap="$3"
                      px="$3"
                      py="$3"
                      br={12}
                      bw={1.5}
                      bc={colors.border}
                      opacity={joined ? 0.55 : 1}
                      cursor={joined ? "default" : "pointer"}
                      pressStyle={joined ? undefined : { opacity: 0.75 }}
                      onPress={() => !joined && grantCard(c)}>
                      <CardBrand bincode={c.card?.bincode} />
                      <YStack f={1} miw={0}>
                        <Text fontSize={14} fontWeight="700" color={colors.text}>
                          信用卡 •••• {c.card?.lastfour ?? "----"}
                        </Text>
                        <Text fontSize={11} color={colors.sub} mt="$0.5">
                          {c.card?.issuer || c.nickname || "信用卡"}
                        </Text>
                      </YStack>
                      <Text fontSize={12} fontWeight="700" color={joined ? colors.hint : colors.primary}>
                        {joined ? "已加入" : busy ? "加入中…" : "加入 ›"}
                      </Text>
                    </XStack>
                  );
                })}
              </YStack>
            )}

            {/* 沒有想用的卡 → 走原本 TapPay 綁新卡 */}
            <XStack
              ai="center"
              jc="center"
              br={12}
              py="$3"
              bw={1.5}
              bc={colors.border}
              borderStyle="dashed"
              cursor="pointer"
              onPress={() => goBind("personal")}>
              <Text fontSize={14} fontWeight="700" color={colors.primary}>
                ＋ 綁一張新卡
              </Text>
            </XStack>

            <XStack ai="center" jc="center" br={12} py="$3" bw={1} bc={colors.border} cursor="pointer" onPress={() => setSheetStep("menu")}>
              <Text fontSize={14} fontWeight="700" color={colors.sub}>
                返回
              </Text>
            </XStack>
          </YStack>
        ) : (
        <YStack px="$4" pb="$5" gap="$3.5">
          <XStack ai="center" gap="$2.5">
            <View w={36} h={36} br={9} bg={colors.primaryBg} ai="center" jc="center">
              <RiBankCardLine size={18} color={colors.primary} />
            </View>
            <Text fontSize={17} fontWeight="800" color={colors.text}>
              新增付款卡
            </Text>
          </XStack>

          <Text fontSize={12} color={colors.sub}>
            從既有卡片加入，或以 TapPay 安全綁定新卡。
          </Text>

          <YStack gap="$2.5">
            {/* 路徑 A：從個人錢包加入（既有卡授權，或綁新個人卡） */}
            <YStack
              bg={colors.bg}
              br={12}
              px="$3.5"
              py="$3.5"
              gap="$1.5"
              bw={1}
              bc={colors.border}
              cursor="pointer"
              onPress={openPickCard}
            >
              <XStack ai="center" jc="space-between">
                <Text fontSize={14} fontWeight="800" color={colors.text}>
                  從個人錢包加入
                </Text>
                <Text fontSize={12} fontWeight="700" color={colors.primary}>
                  選卡 ›
                </Text>
              </XStack>
              <Text fontSize={12} color={colors.sub} lineHeight={18}>
                選一張你已綁定的卡加入此帳戶，或綁一張新的個人卡；卡片屬於你個人，可跨所有帳戶使用。
              </Text>
            </YStack>

            {/* 路徑 B：為此帳戶綁新卡（context 公司卡，需負責人權限） */}
            <YStack
              bg={colors.bgPurple}
              br={12}
              px="$3.5"
              py="$3.5"
              gap="$1.5"
              bw={1}
              bc={colors.bgPurple}
              cursor="pointer"
              onPress={() => goBind("context")}
            >
              <XStack ai="center" jc="space-between">
                <Text fontSize={14} fontWeight="800" color={colors.purpleDark}>
                  為此帳戶綁新卡
                </Text>
                <Text fontSize={12} fontWeight="700" color={colors.purpleDark}>
                  綁定 ›
                </Text>
              </XStack>
              <Text fontSize={12} color={colors.purpleDark} lineHeight={18}>
                卡片只屬於此帳戶，所有成員皆可使用、不隨成員去留消失（限帳戶負責人）。
              </Text>
            </YStack>
          </YStack>

          <InfoNote tone="amber">「帳戶專屬卡」只有帳戶負責人可新增；一般成員請用「從個人錢包加入」。</InfoNote>

          <XStack ai="center" jc="center" br={12} py="$3" bw={1} bc={colors.border} cursor="pointer" onPress={closeSheet}>
            <Text fontSize={14} fontWeight="700" color={colors.sub}>
              取消
            </Text>
          </XStack>
        </YStack>
        )}
      </BottomSheet>
    </YStack>
  );
};

export default AccountPayments;
